import { cn } from '@/lib/utils';

export type IndicatorVariant = 'success' | 'warning' | 'error';

const dotVariantClasses: Record<IndicatorVariant, string> = {
  success: 'bg-green-500',
  warning: 'bg-amber-500',
  error: 'bg-destructive',
};

type IndicatorProps = {
  visible?: boolean;
  className?: string;
  children?: React.ReactNode;
};

export function Indicator({ visible = true, className, children }: IndicatorProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center transition-opacity duration-500',
        visible ? 'opacity-100' : 'opacity-0',
        className,
      )}
    >
      {children}
    </span>
  );
}

type IndicatorDotProps = {
  variant: IndicatorVariant;
  className?: string;
};

export function IndicatorDot({ variant, className }: IndicatorDotProps) {
  return (
    <span
      className={cn('inline-block h-2 w-2 shrink-0 rounded-full', dotVariantClasses[variant], className)}
      aria-hidden
    />
  );
}

type ComposedIndicatorProps = {
  visible?: boolean;
  className?: string;
  dotClassName?: string;
};

function createComposedIndicator(variant: IndicatorVariant, defaultClassName?: string) {
  return function ComposedIndicator({ visible = true, className, dotClassName }: ComposedIndicatorProps) {
    return (
      <Indicator visible={visible} className={cn(defaultClassName, className)}>
        <IndicatorDot variant={variant} className={dotClassName} />
      </Indicator>
    );
  };
}

export const SuccessIndicator = createComposedIndicator('success', 'ml-2');
export const WarningIndicator = createComposedIndicator('warning');
export const ErrorIndicator = createComposedIndicator('error');
